export interface TrustedResourceUrlOptions {
  allowedHosts?: readonly string[];
  allowSameOrigin?: boolean;
  allowExternalHttps?: boolean;
}

const DEFAULT_ORIGIN = 'http://localhost';

function getBaseOrigin(): string {
  if (globalThis.window === undefined) {
    return DEFAULT_ORIGIN;
  }

  return globalThis.window.location?.origin || DEFAULT_ORIGIN;
}

function isAbsoluteUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

function isHttpProtocol(protocol: string): boolean {
  return protocol === 'http:' || protocol === 'https:';
}

export function toTrustedResourceUrl(
  rawUrl: string,
  options: TrustedResourceUrlOptions = {},
): string | null {
  const normalized = rawUrl.trim();

  if (!normalized || normalized.startsWith('//')) {
    return null;
  }

  const absoluteUrl = isAbsoluteUrl(normalized);
  const baseOrigin = getBaseOrigin();

  let parsedUrl: URL;

  try {
    parsedUrl = absoluteUrl ? new URL(normalized) : new URL(normalized, baseOrigin);
  } catch {
    return null;
  }

  if (!isHttpProtocol(parsedUrl.protocol)) {
    return null;
  }

  if (parsedUrl.username || parsedUrl.password) {
    return null;
  }

  const allowSameOrigin = options.allowSameOrigin ?? true;
  const allowExternalHttps = options.allowExternalHttps ?? false;
  const allowedHosts = new Set((options.allowedHosts ?? []).map((host) => host.toLowerCase()));
  const isSameOrigin = parsedUrl.origin === baseOrigin;
  const isAllowedHost = allowedHosts.has(parsedUrl.hostname.toLowerCase());
  const isExternalHttps = parsedUrl.protocol === 'https:';

  if (!isAllowedHost && !(allowSameOrigin && isSameOrigin) && !(allowExternalHttps && isExternalHttps)) {
    return null;
  }

  return absoluteUrl ? parsedUrl.toString() : normalized;
}